import type { DragEvent, KeyboardEvent, MouseEvent } from 'react'
import { Camera, Plus, Check, List, Grid2x2, Grid3x3, LayoutGrid } from 'lucide-react'
import { apiClient } from '../lib/api-client'
import type { MergedPhotoItem } from '../lib/merge-photo-items'
import type { UploadProgress } from '../lib/hooks'
import type { PhotoGridNavigation } from '../hooks/use-photo-grid-navigation'

export type ViewMode = 'list' | 'small' | 'medium' | 'large'

export interface PhotoGalleryProps {
  items: MergedPhotoItem[]
  currentFolderId: string
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  selectedPhotos: Set<string>
  onPhotoSelect: (fileId: string, index: number, event: MouseEvent) => void
  onCheckboxClick: (fileId: string, index: number, event: MouseEvent) => void
  isDropzoneActive: boolean
  onDropzoneDragOver: (e: DragEvent) => void
  onDropzoneDragLeave: (e: DragEvent) => void
  onDropzoneDrop: (e: DragEvent) => void
  onAddMore: () => void
  uploadProgress: UploadProgress
  /**
   * Roving-tabindex keyboard navigation (SOL-82). Optional: the public view
   * and the older tests render this gallery without it, and a grid with no
   * navigation behaves exactly as it did before -- clickable, not focusable.
   */
  navigation?: PhotoGridNavigation
}

// Presentational only: the gallery header (count, view-mode buttons, "Add
// More") plus the grid and its per-tile rendering (checkbox, the three-way
// image branch, upload progress ring, done checkmark, filename). Every
// handler arrives as a prop; selection and upload state live in
// album-list.tsx's hooks (SOL-150).
export default function PhotoGallery({
  items,
  currentFolderId,
  viewMode,
  onViewModeChange,
  selectedPhotos,
  onPhotoSelect,
  onCheckboxClick,
  isDropzoneActive,
  onDropzoneDragOver,
  onDropzoneDragLeave,
  onDropzoneDrop,
  onAddMore,
  uploadProgress,
  navigation,
}: PhotoGalleryProps) {
  return (
    <div
      className={`photo-gallery ${isDropzoneActive ? 'photo-gallery--dragover' : ''}`}
      onDragOver={onDropzoneDragOver}
      onDragLeave={onDropzoneDragLeave}
      onDrop={onDropzoneDrop}
    >
      <div className="photo-gallery-header">
        <span className="photo-count">
          {items.length} {items.length === 1 ? 'memory' : 'memories'}
        </span>
        <div className="view-mode-selector">
          <button
            className={`view-mode-btn ${viewMode === 'list' ? 'view-mode-btn--active' : ''}`}
            onClick={() => onViewModeChange('list')}
            title="List view"
          >
            <List size={16} />
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'small' ? 'view-mode-btn--active' : ''}`}
            onClick={() => onViewModeChange('small')}
            title="Small icons"
          >
            <Grid3x3 size={16} />
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'medium' ? 'view-mode-btn--active' : ''}`}
            onClick={() => onViewModeChange('medium')}
            title="Medium icons"
          >
            <Grid2x2 size={16} />
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'large' ? 'view-mode-btn--active' : ''}`}
            onClick={() => onViewModeChange('large')}
            title="Large icons"
          >
            <LayoutGrid size={16} />
          </button>
        </div>
        <button
          className="add-more-btn"
          onClick={onAddMore}
          disabled={uploadProgress.status !== 'idle' && uploadProgress.status !== 'done' && uploadProgress.status !== 'error'}
        >
          <Plus size={16} />
          <span>Add More</span>
        </button>
      </div>
      <div className={`photo-grid photo-grid--${viewMode}`} ref={navigation?.gridRef}>
        {/* Unified photo list - seamlessly transitions from upload to loaded */}
        {items.map((item, index) => {
          const isExisting = item.type === 'existing' && item.fileId
          const isSelected = isExisting ? selectedPhotos.has(item.fileId!) : false
          const uploadFile = item.uploadFile
          const variant = viewMode === 'small' ? 'thumb' : 'medium'
          const existingSrc = isExisting && item.file
            ? apiClient.getVariantUrl(currentFolderId, item.file, variant)
            : null

          // Determine what to show:
          // - If the signed URL is available, show it
          // - If still uploading or transitioning, show upload preview
          const showUploadPreview = uploadFile && !existingSrc
          const isUploading = uploadFile && uploadFile.status !== 'done'
          const isTransitioning = uploadFile && uploadFile.status === 'done' && !existingSrc

          // A tile is keyboard-operable only when there is something to
          // operate: an in-flight upload has no file id to select, and a
          // focusable tile that ignores every key is worse than one that is
          // skipped. Same rule photo-grid.tsx applies to a tile with no click
          // handler.
          const keyboard = Boolean(isExisting && navigation)

          return (
            <div
              key={item.key}
              className={`photo-item ${isSelected ? 'photo-item--selected' : ''} ${isUploading ? 'photo-item--uploading' : ''} ${isTransitioning ? 'photo-item--transitioning' : ''}`}
              onClick={isExisting ? (e) => {
                // Keep the tab stop under the tile the pointer just used, so
                // Tab returns the user where they were rather than to tile 0.
                navigation?.setFocusedIndex(index)
                onPhotoSelect(item.fileId!, index, e)
              } : undefined}
              onKeyDown={keyboard ? (e: KeyboardEvent<HTMLDivElement>) => navigation!.onKeyDown(e) : undefined}
              // `button` + `aria-pressed`, not listbox/option: an `option`
              // may not contain interactive descendants and this tile nests a
              // checkbox. A toggle button is both valid and true to what the
              // tile does. photo-grid.tsx already sets the precedent.
              role={keyboard ? 'button' : undefined}
              aria-pressed={keyboard ? isSelected : undefined}
              aria-label={keyboard ? item.label : undefined}
              // Roving tabindex: exactly one tile is in the tab order.
              tabIndex={keyboard ? (navigation!.focusedIndex === index ? 0 : -1) : undefined}
              data-photo-index={index}
              data-photo-selectable={keyboard ? true : undefined}
            >
              {/* Selection checkbox - only for existing files */}
              {isExisting && (
                <div
                  className={`photo-checkbox ${isSelected ? 'photo-checkbox--checked' : ''}`}
                  onClick={(e) => onCheckboxClick(item.fileId!, index, e)}
                  // The tile itself is the toggle assistive tech is offered;
                  // announcing this too would be two controls for one action.
                  aria-hidden="true"
                >
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </div>
              )}

              {/* Image display logic */}
              {existingSrc ? (
                // Signed variant URL available - render directly
                <img
                  src={existingSrc}
                  alt={item.label}
                  loading="lazy"
                />
              ) : showUploadPreview && uploadFile.previewUrl ? (
                // Upload preview available - show it
                <img
                  src={uploadFile.previewUrl}
                  alt={item.label}
                  className="photo-preview"
                />
              ) : (
                // No image available - show placeholder
                <div className="photo-placeholder">
                  <Camera size={32} strokeWidth={1.5} />
                </div>
              )}

              {/* Upload progress ring - only while actively uploading */}
              {isUploading && (
                <div className="upload-ring-container">
                  <svg className="upload-ring" viewBox="0 0 36 36">
                    <circle
                      className="upload-ring-bg"
                      cx="18" cy="18" r="16"
                      fill="none"
                      strokeWidth="3"
                    />
                    <circle
                      className="upload-ring-progress"
                      cx="18" cy="18" r="16"
                      fill="none"
                      strokeWidth="3"
                      strokeDasharray={`${uploadFile.progress}, 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}

              {/* Done checkmark - briefly shown after upload completes */}
              {isTransitioning && (
                <div className="upload-done-check">
                  <Check size={24} strokeWidth={3} />
                </div>
              )}

              <div className="photo-name">{item.label}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
